/**
 * EN Error Display Component
 *
 * Displays validation errors from Engaging Networks forms
 * in a consistent, accessible way within the React UI.
 */

import { X } from 'lucide-react';
import type { ENError } from '../services/enAjaxSubmission';

interface ENErrorDisplayProps {
  errors: ENError[];
  onDismiss?: () => void;
  className?: string;
}

export function ENErrorDisplay({ errors, onDismiss, className = '' }: ENErrorDisplayProps) {
  if (errors.length === 0) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`bg-destructive/10 border border-destructive rounded-lg p-4 ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <svg
              className="h-5 w-5 text-destructive flex-shrink-0"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="font-semibold text-destructive">
              {errors.length === 1
                ? 'There was an error with your submission'
                : 'There were errors with your submission'}
            </h3>
          </div>

          <ul className="space-y-1 ml-7">
            {errors.map((error, index) => (
              <li key={index} className="text-sm text-destructive">
                {error.field && <strong>{error.field}: </strong>}
                {error.message}
              </li>
            ))}
          </ul>
        </div>

        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="flex-shrink-0 text-destructive hover:text-destructive/80 transition-colors"
            aria-label="Dismiss error message"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Compact version for inline error display
 */
export function ENErrorInline({ error, className = '' }: { error: string; className?: string }) {
  return (
    <p className={`text-sm text-destructive flex items-center gap-1 ${className}`} role="alert">
      <svg
        className="h-4 w-4 flex-shrink-0"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>{error}</span>
    </p>
  );
}

